import { useCallback, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  Grid,
  RadioGroup,
  Radio,
  FormControlLabel,
  CircularProgress,
  Box,
  Divider,
} from "@mui/material";
import FormField from "components/FormField/FormField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "layouts/DashboardLayout/DashboardLayout";
import DashboardNavbar from "partials/DashboardNavbar/DashboardNavbar";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import { useBitumioContext } from "providers/BitumioContext";
import { EmailIdentityReplyToType, SesVerificationStatus } from "generated/graphql";
import useGetOrganization from "hooks/organization/useGetOrganization";
import useGetOrganizationEmailIdentity from "hooks/organization/useGetOrganizationEmailIdentity";
import useSaveOrganizationEmailIdentity from "hooks/organization/useSaveOrganizationEmailIdentity";
import useDeleteOrganizationEmailIdentity from "hooks/organization/useDeleteOrganizationEmailIdentity";
import useRefreshOrganizationEmailIdentityStatus from "hooks/organization/useRefreshOrganizationEmailIdentityStatus";
import useToggleOrganizationEmailIdentity from "hooks/organization/useToggleOrganizationEmailIdentity";
import useSendOrganizationEmailIdentityTestEmail from "hooks/organization/useSendOrganizationEmailIdentityTestEmail";
import StatusDisplay from "./StatusDisplay";
import DnsRecordsTable from "./DnsRecordsTable";

const emailLocalPartRegex = /^[a-zA-Z0-9._%+-]+$/;
const hostnameRegex = /^(?!-)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

const schema = z.object({
  sendingDomain: z
    .string()
    .min(1, "Enter a valid domain or subdomain, like mail.yourdomain.com.")
    .refine((val) => !val.includes("@") && !val.includes(" "), {
      message: "Enter a valid domain or subdomain, like mail.yourdomain.com.",
    })
    .refine((val) => hostnameRegex.test(val), {
      message: "Enter a valid domain or subdomain, like mail.yourdomain.com.",
    }),
  proposalLocalPart: z
    .string()
    .min(1, "Sender address is required.")
    .refine((val) => !val.includes("@"), { message: "Do not include @." })
    .refine((val) => emailLocalPartRegex.test(val), {
      message: "Only letters, numbers, dots, underscores, percent, plus, and hyphens allowed.",
    }),
  invoiceLocalPart: z
    .string()
    .min(1, "Invoice sender address is required.")
    .refine((val) => !val.includes("@"), { message: "Do not include @." })
    .refine((val) => emailLocalPartRegex.test(val), {
      message: "Only letters, numbers, dots, underscores, percent, plus, and hyphens allowed.",
    }),
  notificationsLocalPart: z
    .string()
    .min(1, "Notifications sender address is required.")
    .refine((val) => !val.includes("@"), { message: "Do not include @." })
    .refine((val) => emailLocalPartRegex.test(val), {
      message: "Only letters, numbers, dots, underscores, percent, plus, and hyphens allowed.",
    }),
  fromName: z.string().optional(),
  replyToType: z.nativeEnum(EmailIdentityReplyToType),
  replyToCustomEmail: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

export default function EmailSendingPage() {
  const [{ signedInUser }] = useBitumioContext();
  const { data: orgData } = useGetOrganization();

  const { identity, loading: queryLoading } = useGetOrganizationEmailIdentity();
  const [saveOrganizationEmailIdentity, { loading: saveLoading }] =
    useSaveOrganizationEmailIdentity();
  const [deleteOrganizationEmailIdentity, { loading: deleteLoading }] =
    useDeleteOrganizationEmailIdentity();
  const [handleRefreshStatus, { loading: refreshLoading }] =
    useRefreshOrganizationEmailIdentityStatus();
  const [handleToggle] = useToggleOrganizationEmailIdentity();
  const [handleSendTestEmail, { loading: testEmailLoading }] =
    useSendOrganizationEmailIdentityTestEmail();

  const [confirmDomainChange, setConfirmDomainChange] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const orgName = orgData?.name ?? signedInUser?.organization?.name ?? "";
  const orgEmail = orgData?.email ?? "";

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      sendingDomain: identity?.sendingDomain ?? "",
      proposalLocalPart: identity?.proposalLocalPart ?? "proposals",
      invoiceLocalPart: identity?.invoiceLocalPart ?? "invoices",
      notificationsLocalPart: identity?.notificationsLocalPart ?? "notifications",
      fromName: identity?.fromName ?? "",
      replyToType: identity?.replyToType ?? EmailIdentityReplyToType.estimator,
      replyToCustomEmail: identity?.replyToCustomEmail ?? "",
    },
    values: identity
      ? {
          sendingDomain: identity.sendingDomain,
          proposalLocalPart: identity.proposalLocalPart,
          invoiceLocalPart: identity.invoiceLocalPart,
          notificationsLocalPart: identity.notificationsLocalPart ?? "notifications",
          fromName: identity.fromName ?? "",
          replyToType: identity.replyToType as EmailIdentityReplyToType,
          replyToCustomEmail: identity.replyToCustomEmail ?? "",
        }
      : undefined,
  });

  const watchDomain = watch("sendingDomain");
  const watchProposalLocalPart = watch("proposalLocalPart");
  const watchInvoiceLocalPart = watch("invoiceLocalPart");
  const watchNotificationsLocalPart = watch("notificationsLocalPart");
  const watchReplyToType = watch("replyToType");
  const watchFromName = watch("fromName");

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (identity && identity.sendingDomain && values.sendingDomain !== identity.sendingDomain) {
        setConfirmDomainChange(true);
        return;
      }
      await saveOrganizationEmailIdentity({
        variables: {
          input: {
            sendingDomain: values.sendingDomain,
            proposalLocalPart: values.proposalLocalPart,
            invoiceLocalPart: values.invoiceLocalPart,
            notificationsLocalPart: values.notificationsLocalPart,
            fromName: values.fromName || null,
            replyToType: values.replyToType,
            replyToCustomEmail:
              values.replyToType === EmailIdentityReplyToType.custom
                ? values.replyToCustomEmail
                : null,
          },
        },
      });
    },
    [identity, saveOrganizationEmailIdentity]
  );

  const handleConfirmDomainChange = handleSubmit(async (values) => {
    setConfirmDomainChange(false);
    await saveOrganizationEmailIdentity({
      variables: {
        input: {
          sendingDomain: values.sendingDomain,
          proposalLocalPart: values.proposalLocalPart,
          invoiceLocalPart: values.invoiceLocalPart,
          notificationsLocalPart: values.notificationsLocalPart,
          fromName: values.fromName || null,
          replyToType: values.replyToType,
          replyToCustomEmail:
            values.replyToType === EmailIdentityReplyToType.custom
              ? values.replyToCustomEmail
              : null,
        },
      },
    });
  });

  const handleDelete = useCallback(async () => {
    setConfirmDelete(false);
    const result = await deleteOrganizationEmailIdentity({});
    if (result?.success) {
      reset({
        sendingDomain: "",
        proposalLocalPart: "proposals",
        invoiceLocalPart: "invoices",
        notificationsLocalPart: "notifications",
        fromName: "",
        replyToType: EmailIdentityReplyToType.estimator,
        replyToCustomEmail: "",
      });
    }
  }, [deleteOrganizationEmailIdentity, reset]);

  const copyToClipboard = useCallback((value: string, fieldId: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
  }, []);

  const saveButtonLabel = useMemo(() => {
    if (!identity) return "Save and generate DNS records";
    if (identity.verificationStatus !== SesVerificationStatus.VERIFIED)
      return "Save and regenerate DNS records";
    return "Save changes";
  }, [identity]);

  const replyToPreviewLabel = useMemo(() => {
    if (watchReplyToType === EmailIdentityReplyToType.estimator)
      return "The team member sending the email";
    if (watchReplyToType === EmailIdentityReplyToType.organization) return orgEmail || orgName;
    return watch("replyToCustomEmail") || "";
  }, [watchReplyToType, orgEmail, orgName, watch]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        {queryLoading ? (
          <MDBox display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </MDBox>
        ) : (
          <MDBox>
            <Card sx={{ mb: 3 }}>
              <MDBox padding={4} component="form" role="form" onSubmit={handleSubmit(onSubmit)}>
                <MDTypography variant="h5" fontWeight="medium" mb={0.5}>
                  Custom Email Sending
                </MDTypography>
                <MDTypography variant="body2" mb={3}>
                  Send proposals and invoices from your own domain while Bitumio handles delivery
                  through our secure email infrastructure.
                </MDTypography>

                <Grid container spacing={4}>
                  <Grid item xs={12} lg={6}>
                    <MDTypography variant="subtitle2" fontWeight="medium" mb={2}>
                      Sending domain &amp; addresses
                    </MDTypography>

                    <MDBox mb={2}>
                      <Controller
                        name="sendingDomain"
                        control={control}
                        render={({ field }) => (
                          <FormField
                            {...field}
                            label="Sending domain"
                            placeholder="mail.yourdomain.com"
                            error={errors.sendingDomain}
                          />
                        )}
                      />
                      <MDTypography variant="caption" mt={0.75} display="block">
                        Use a dedicated subdomain like <strong>mail.yourcompany.com</strong>,
                        it&apos;s easier to set up and won&apos;t affect your main company email.
                      </MDTypography>
                    </MDBox>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <MDTypography
                          variant="caption"
                          fontWeight="medium"
                          mb={0.5}
                          display="block"
                        >
                          Proposals &amp; estimates
                        </MDTypography>
                        <Box display="flex" alignItems="flex-start" gap={1}>
                          <Controller
                            name="proposalLocalPart"
                            control={control}
                            render={({ field }) => (
                              <FormField
                                {...field}
                                placeholder="proposals"
                                error={errors.proposalLocalPart}
                              />
                            )}
                          />
                          <MDTypography variant="body2" mt={1.25} sx={{ whiteSpace: "nowrap" }}>
                            @{watchDomain || "yourdomain.com"}
                          </MDTypography>
                        </Box>
                        {watchProposalLocalPart && watchDomain && (
                          <MDTypography variant="caption" color="info" mt={0.5} display="block">
                            {watchProposalLocalPart}@{watchDomain}
                          </MDTypography>
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <MDTypography
                          variant="caption"
                          fontWeight="medium"
                          mb={0.5}
                          display="block"
                        >
                          Invoices
                        </MDTypography>
                        <Box display="flex" alignItems="flex-start" gap={1}>
                          <Controller
                            name="invoiceLocalPart"
                            control={control}
                            render={({ field }) => (
                              <FormField
                                {...field}
                                placeholder="invoices"
                                error={errors.invoiceLocalPart}
                              />
                            )}
                          />
                          <MDTypography variant="body2" mt={1.25} sx={{ whiteSpace: "nowrap" }}>
                            @{watchDomain || "yourdomain.com"}
                          </MDTypography>
                        </Box>
                        {watchInvoiceLocalPart && watchDomain && (
                          <MDTypography variant="caption" color="info" mt={0.5} display="block">
                            {watchInvoiceLocalPart}@{watchDomain}
                          </MDTypography>
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <MDTypography
                          variant="caption"
                          fontWeight="medium"
                          mb={0.5}
                          display="block"
                        >
                          Notifications
                        </MDTypography>
                        <Box display="flex" alignItems="flex-start" gap={1}>
                          <Controller
                            name="notificationsLocalPart"
                            control={control}
                            render={({ field }) => (
                              <FormField
                                {...field}
                                placeholder="notifications"
                                error={errors.notificationsLocalPart}
                              />
                            )}
                          />
                          <MDTypography variant="body2" mt={1.25} sx={{ whiteSpace: "nowrap" }}>
                            @{watchDomain || "yourdomain.com"}
                          </MDTypography>
                        </Box>
                        {watchNotificationsLocalPart && watchDomain && (
                          <MDTypography variant="caption" color="info" mt={0.5} display="block">
                            Automated emails will be sent from {watchNotificationsLocalPart}@
                            {watchDomain}
                          </MDTypography>
                        )}
                        <MDTypography variant="caption" mt={0.5} display="block">
                          This is the address used for automated triggers, such as follow-ups and
                          reminders.
                        </MDTypography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} lg={6}>
                    <MDTypography variant="subtitle2" fontWeight="medium" mb={2}>
                      Sender identity
                    </MDTypography>

                    <MDBox mb={3}>
                      <Controller
                        name="fromName"
                        control={control}
                        render={({ field }) => (
                          <FormField
                            {...field}
                            label="From name"
                            placeholder={orgName}
                            error={errors.fromName}
                          />
                        )}
                      />
                      <MDTypography variant="caption" mt={0.75} display="block">
                        The sender name customers see in their inbox. Defaults to your organization
                        name if left blank.
                      </MDTypography>
                    </MDBox>

                    <MDTypography variant="subtitle2" fontWeight="medium" mb={0.5}>
                      Replies go to
                    </MDTypography>
                    <MDTypography variant="caption" mb={1} display="block">
                      Controls where customer replies land. Does not affect the visible sender
                      address.
                    </MDTypography>
                    <Controller
                      name="replyToType"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup {...field}>
                          <FormControlLabel
                            value={EmailIdentityReplyToType.estimator}
                            control={<Radio size="small" />}
                            label="The team member sending the email"
                          />
                          <FormControlLabel
                            value={EmailIdentityReplyToType.organization}
                            control={<Radio size="small" />}
                            label="My organization email"
                          />
                          <FormControlLabel
                            value={EmailIdentityReplyToType.custom}
                            control={<Radio size="small" />}
                            label="A custom email address"
                          />
                        </RadioGroup>
                      )}
                    />
                    {watchReplyToType === EmailIdentityReplyToType.custom && (
                      <MDBox mt={1.5}>
                        <Controller
                          name="replyToCustomEmail"
                          control={control}
                          rules={{
                            required: "Enter a valid email address.",
                            pattern: {
                              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                              message: "Enter a valid email address.",
                            },
                          }}
                          render={({ field }) => (
                            <FormField
                              {...field}
                              label="Custom reply-to email"
                              placeholder="sales@yourdomain.com"
                              error={errors.replyToCustomEmail}
                            />
                          )}
                        />
                      </MDBox>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                    <Box display="flex" gap={2} alignItems="center" pt={2}>
                      <MDButton
                        type="submit"
                        color="info"
                        variant="gradient"
                        disabled={saveLoading}
                      >
                        {saveLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          saveButtonLabel
                        )}
                      </MDButton>
                      {identity && (
                        <MDButton
                          color="error"
                          variant="outlined"
                          onClick={() => setConfirmDelete(true)}
                          disabled={deleteLoading}
                        >
                          Remove domain
                        </MDButton>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>

            <Card sx={{ mb: 3 }}>
              <MDBox padding={4}>
                <MDTypography variant="h5" fontWeight="medium" mb={2}>
                  Verification status
                </MDTypography>
                <StatusDisplay
                  identity={identity}
                  refreshLoading={refreshLoading}
                  onRefresh={handleRefreshStatus}
                  onToggle={handleToggle}
                  onSendTestEmail={handleSendTestEmail}
                  testEmailLoading={testEmailLoading}
                />
              </MDBox>
            </Card>

            {identity && identity.verificationStatus !== SesVerificationStatus.VERIFIED && (
              <Card sx={{ mb: 3 }}>
                <MDBox padding={4}>
                  <MDTypography variant="h5" fontWeight="medium" mb={0.5}>
                    DNS records to add
                  </MDTypography>
                  <MDTypography variant="body2" mb={0.5}>
                    Add these records at your DNS provider to verify your domain. DNS changes can
                    take some time to propagate.
                  </MDTypography>
                  <MDTypography variant="caption" mb={3} display="block">
                    After adding the records, click <strong>Refresh status</strong>. Verification
                    may take a few minutes, and sometimes up to 24 hours.
                  </MDTypography>
                  <DnsRecordsTable
                    identity={identity}
                    copiedField={copiedField}
                    onCopy={copyToClipboard}
                  />
                  <MDTypography variant="caption" mt={2} display="block">
                    Need help? Send these records to your IT team or domain administrator.
                  </MDTypography>
                </MDBox>
              </Card>
            )}

            {identity && (
              <Card>
                <MDBox padding={4}>
                  <MDTypography variant="h5" fontWeight="medium" mb={0.5}>
                    Preview
                  </MDTypography>
                  <MDTypography variant="body2" mb={2}>
                    How your emails will appear when custom sending is active.
                  </MDTypography>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: (theme) => (theme.palette.mode === "dark" ? "grey.900" : "grey.50"),
                      fontFamily: "monospace",
                      maxWidth: 520,
                    }}
                  >
                    <MDTypography
                      variant="body2"
                      fontFamily="monospace"
                      sx={{ display: "flex", gap: 1 }}
                    >
                      <strong style={{ minWidth: 72, opacity: 0.6 }}>From</strong>
                      {watchFromName || orgName} &lt;{identity.proposalFromAddress}&gt;
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      fontFamily="monospace"
                      mt={0.5}
                      sx={{ display: "flex", gap: 1 }}
                    >
                      <strong style={{ minWidth: 72, opacity: 0.6 }}>Notifications</strong>
                      {identity.notificationsFromAddress ??
                        `${watchNotificationsLocalPart || "notifications"}@${
                          watchDomain || "yourdomain.com"
                        }`}
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      fontFamily="monospace"
                      mt={0.5}
                      sx={{ display: "flex", gap: 1 }}
                    >
                      <strong style={{ minWidth: 72, opacity: 0.6 }}>Reply-To</strong>
                      {replyToPreviewLabel}
                    </MDTypography>
                  </Box>
                  {identity.verificationStatus !== SesVerificationStatus.VERIFIED && (
                    <MDTypography variant="caption" mt={1.5} display="block">
                      If verification is incomplete, Bitumio will temporarily send from its default
                      address until your domain is ready.
                    </MDTypography>
                  )}
                </MDBox>
              </Card>
            )}
          </MDBox>
        )}
      </MDBox>

      <ActionConfirmDialog
        open={confirmDomainChange}
        setOpen={setConfirmDomainChange}
        handleCancel={() => setConfirmDomainChange(false)}
        handleAccept={handleConfirmDomainChange}
        title="Change sending domain?"
        content="Changing the sending domain will reset verification and require you to re-add DNS records. Do you want to continue?"
        acceptText="Continue"
        cancelText="Cancel"
      />

      <ActionConfirmDialog
        open={confirmDelete}
        setOpen={setConfirmDelete}
        handleCancel={() => setConfirmDelete(false)}
        handleAccept={handleDelete}
        title="Remove custom sending domain?"
        content="This will remove your custom sending domain configuration. Emails will be sent from Bitumio's default sender."
        acceptText="Remove"
        cancelText="Cancel"
      />
    </DashboardLayout>
  );
}
